// Filter state for the costs SPA. Default `month` comes from
// `/api/costs/latest-month` so it matches data, not "today − 1 month".

import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type LatestMonthResponse, type AvailableMonthsResponse } from '@/lib/api';

export interface Filters {
  month: string;
  fromMonth?: string;
}

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function isoMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function clientFallbackMonth(): string {
  // Used only if /latest-month is unreachable.
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return isoMonth(d);
}

// Backwards compat for old import sites.
export const latestClosedMonth = clientFallbackMonth;

// Persist the selected month for the browser session so it carries across every
// page (plain nav links drop the ?month query). sessionStorage (not local) so a
// fresh session still defaults to the latest month.
const SESSION_MONTH_KEY = 'kc-month';
function readSessionMonth(): string | null {
  try { return sessionStorage.getItem(SESSION_MONTH_KEY); } catch { return null; }
}
function writeSessionMonth(iso: string): void {
  try { sessionStorage.setItem(SESSION_MONTH_KEY, iso); } catch { /* private mode / disabled */ }
}

export function monthLabel(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return iso;
  return `${monthNames[m - 1]} ${y}`;
}

export function monthYear(iso: string): { month: number; year: number } {
  const [y, m] = iso.split('-').map(Number);
  return { month: m, year: y };
}

export function yearMonthIso(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

// Returns the ISO month string one month earlier (e.g. '2026-05' → '2026-04',
// '2026-01' → '2025-12'). Used for "vs previous month" delta calculations
// that need to follow the user-selected filter month.
export function prevMonthIso(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return iso;
  const prevM = m === 1 ? 12 : m - 1;
  const prevY = m === 1 ? y - 1 : y;
  return yearMonthIso(prevY, prevM);
}

export function useLatestMonth() {
  return useQuery({
    queryKey: ['costs-latest-month'],
    queryFn: () => api<LatestMonthResponse>('/latest-month'),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
}

// Months with data ('YYYY-MM' ascending). Drives the FilterStrip year/month
// dropdowns so users can only pick months that actually exist.
export function useAvailableMonths() {
  return useQuery({
    queryKey: ['costs-available-months'],
    queryFn: () => api<AvailableMonthsResponse>('/available-months'),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
}

export function useFilters(): {
  filters: Filters;
  setMonth: (iso: string) => void;
  setLatest: () => void;
  toQueryString: () => string;
} {
  const [params, setParams] = useSearchParams();
  const { data: latest } = useLatestMonth();
  const urlMonth = params.get('month');
  const sessionMonth = readSessionMonth();
  // Precedence: explicit URL month (shared/bookmarked links) > last in-session
  // selection (carries across pages) > server latest.
  const month = urlMonth || sessionMonth || latest?.month || clientFallbackMonth();

  // Keep URL + session in sync with the resolved month so every page — including
  // plain nav links that drop the ?month query — shows the same selection.
  useEffect(() => {
    if (urlMonth) {
      // Explicit URL month wins and becomes the remembered selection.
      if (urlMonth !== sessionMonth) writeSessionMonth(urlMonth);
      return;
    }
    // No URL month. Use the in-session pick if present; otherwise wait for
    // /latest-month to resolve (don't lock in the client May-fallback early).
    const resolved = sessionMonth || latest?.month;
    if (!resolved) return;
    writeSessionMonth(resolved);
    const next = new URLSearchParams(params);
    next.set('month', resolved);
    setParams(next, { replace: true });
  }, [urlMonth, sessionMonth, latest?.month, params, setParams]);

  const filters = useMemo<Filters>(() => ({ month }), [month]);

  const setMonth = useCallback((iso: string) => {
    writeSessionMonth(iso);
    const next = new URLSearchParams(params);
    next.set('month', iso);
    setParams(next, { replace: false });
  }, [params, setParams]);

  const setLatest = useCallback(() => {
    setMonth(latest?.month ?? clientFallbackMonth());
  }, [latest?.month, setMonth]);

  const toQueryString = useCallback(() => {
    const next = new URLSearchParams();
    next.set('month', filters.month);
    return next.toString();
  }, [filters]);

  return { filters, setMonth, setLatest, toQueryString };
}
